import type { EmployeeEarningAssignment, NsPayrollItem } from '@worklogix/shared';

/**
 * Pure mapping from synced NetSuite payroll (earning-type) items to the admin
 * picker's options (plan-v8 WEB #3). The wrong earning type is wrong pay, so the
 * option/label mapping is factored out here and unit-tested rather than inlined
 * in JSX.
 */

/** One dropdown option for the payroll-item picker. */
export interface PayrollOption {
  /** The `nsPayrollItemId` used as the option value. */
  value: string;
  /** Display label, e.g. `Regular (84)` — id in parens disambiguates like names. */
  label: string;
  active: boolean;
}

/** Format a single item's display label (name + id, with an inactive marker). */
function formatLabel(item: NsPayrollItem): string {
  const base = `${item.name} (${item.nsPayrollItemId})`;
  return item.active ? base : `${base} — inactive`;
}

/**
 * Map synced payroll items to options: ACTIVE items first (alphabetical by
 * label), then inactive ones (still selectable so an already-mapped inactive id
 * remains visible), also alphabetical. Input is never mutated.
 */
export function toPayrollOptions(items: readonly NsPayrollItem[]): PayrollOption[] {
  return [...items]
    .map((item) => ({
      value: item.nsPayrollItemId,
      label: formatLabel(item),
      active: item.active,
    }))
    .sort((a, b) => {
      if (a.active !== b.active) {
        return a.active ? -1 : 1;
      }
      return a.label.localeCompare(b.label);
    });
}

/**
 * Resolve the display label for a currently-selected payroll id. Returns an
 * empty string for an unset selection, and a clearly-flagged fallback when the
 * selected id is not in the synced list (e.g. a stale mapping or a not-yet-synced
 * item) so the admin sees the raw id rather than a blank.
 */
export function payrollLabel(
  items: readonly NsPayrollItem[],
  selectedId: string | null | undefined
): string {
  if (selectedId === null || selectedId === undefined || selectedId === '') {
    return '';
  }
  const match = items.find((item) => item.nsPayrollItemId === selectedId);
  return match !== undefined ? formatLabel(match) : `${selectedId} (not in synced list)`;
}

// ── an employee's HELD earnings (the roster "Payroll item" column) ────────────

/** One assignment's label: the joined payroll-item name, else the raw NS id. */
export function earningAssignmentLabel(a: EmployeeEarningAssignment): string {
  return a.name !== null && a.name !== '' ? a.name : `NS #${a.nsPayrollItemId}`;
}

/**
 * Index every assignment by `employeeId` in ONE pass, so a roster of ~90 rows
 * does a map lookup per row instead of re-filtering the whole list 90 times.
 * Order within a bucket is preserved (the API already sorts by name).
 */
export function groupEarningsByEmployee(
  assignments: readonly EmployeeEarningAssignment[]
): Map<string, EmployeeEarningAssignment[]> {
  const byEmployee = new Map<string, EmployeeEarningAssignment[]>();
  for (const a of assignments) {
    const bucket = byEmployee.get(a.employeeId);
    if (bucket === undefined) {
      byEmployee.set(a.employeeId, [a]);
    } else {
      bucket.push(a);
    }
  }
  return byEmployee;
}

/** What the "Payroll item" cell renders for one employee. */
export interface EarningCell {
  /** Short, SINGLE-LINE label for the cell. */
  label: string;
  /** Every earning the employee holds, one per line — the cell's tooltip. */
  detail: string;
  /**
   * True when the employee has no WAGE earning, so their clocked hours have
   * nothing to post under. The actionable gap, worth colouring.
   */
  none: boolean;
}

/**
 * Summarize an employee's earnings for the roster cell — WAGE earnings only.
 *
 * WHY WAGE ONLY: the mirrored sublist holds every kind of payroll item at once
 * (BONUS, HOLIDAY, ER HEALTH COST, expense reimbursement…). Listing all of them
 * made this column three or four wrapped lines per row of mostly-irrelevant text,
 * and buried the one value the column is for — what CLOCKED HOURS are paid under.
 * Nothing is hidden: every held earning is still in `detail`, on hover.
 *
 * The three outcomes are deliberately distinct, because they need different
 * actions from payroll:
 *   - a wage earning       → the normal case, named;
 *   - earnings but NO wage → they can be paid a bonus but not their hours;
 *   - nothing at all       → they are not set up in NetSuite payroll.
 *
 * `legacyLabel` (the old hand-set single mapping) is the last resort, so a value
 * that used to be visible never silently disappears.
 */
export function summarizeEmployeeEarnings(
  held: readonly EmployeeEarningAssignment[],
  wageItemIds: ReadonlySet<string>,
  legacyLabel = ''
): EarningCell {
  const all = held.map(earningAssignmentLabel);
  const detailOf = (lead: string): string =>
    all.length === 0 ? lead : `${lead}\n\nHolds in NetSuite:\n${all.join('\n')}`;

  const wage = held.filter((a) => wageItemIds.has(a.nsPayrollItemId)).map(earningAssignmentLabel);

  if (wage.length > 0) {
    // More than one wage earning is itself a problem (the resolver calls it
    // ambiguous), so the count is shown rather than hidden behind a join.
    const label = wage.length === 1 ? (wage[0] as string) : `${wage.length} wage earnings`;
    return { label, detail: detailOf(label), none: false };
  }

  if (all.length > 0) {
    return {
      label: 'No wage earning',
      detail: detailOf(
        'NetSuite lists earnings for this employee but none pays worked time, so their clocked hours have nothing to post under.'
      ),
      none: true,
    };
  }

  return legacyLabel === ''
    ? {
        label: 'None in NetSuite',
        detail: 'NetSuite lists no earning type at all for this employee.',
        none: true,
      }
    : {
        label: legacyLabel,
        detail: `Mapped by hand: ${legacyLabel}. NetSuite lists no earning type for this employee.`,
        none: true,
      };
}
